import i18next from "i18next";
import getPercentileColor from "../../util/getPercentileColor";
import SRS from "../../../../services/StudentRecordService";
import { getUserData } from 'Services/API';
import { formatScoreByType } from './generateScore';
import { MasteryPacing, MasteryPacingColor } from '../../../../enums/MasteryStatus';

const SUBSCORE_SCALES = [
  'decoding',
  'phonologicalAwareness',
  'highFrequencyWords',
  'backgroundKnowledge',
  'structuresAndReasoning',
  'vocabulary',
  'armScore'
];

// Exported utility functions for reuse
export function getPacingFromCutlines(percentile, cutlines) {
  if (percentile == null) return null;

  // Sort cutlines from lowest to highest percentile (upper bounds)
  const sorted = [...cutlines].sort((a, b) => a.percentile - b.percentile);

  // Find the first cutline where the input percentile is < the upper bound
  for (let i = 0; i < sorted.length; i++) {
    if (percentile < sorted[i].percentile) {
      return sorted[i];
    }
  }

  // If percentile >= all cutoffs (including the highest), return the highest band
  return sorted[sorted.length - 1] || null;
}

export function keyToLabel(key) {
  switch (key) {
    case 'decoding': return 'Decoding';
    case 'phonologicalAwareness': return 'Phonological Awareness';
    case 'highFrequencyWords': return 'High Frequency Words';
    case 'backgroundKnowledge': return 'Background Knowledge';
    case 'structuresAndReasoning': return 'Structure & Reasoning';
    case 'vocabulary': return 'Vocabulary';
    default: return key;
  }
}

export function buildSubscore(percentile, key, cutlineMap, translationFunction) {
  if (percentile == null) return null;
  
  const cutline = getPacingFromCutlines(percentile, cutlineMap[key] || []);
  const t = translationFunction || i18next.getFixedT(null, 'teacher_parent_report');
  
  // If we have valid cutlines, use them; otherwise show no data
  if (cutline && cutline.hexCode) {
    return {
      percentile,
      color: cutline.hexCode,
      statusText: t(cutline.cutoffLabel || ''),
      description: t(`domain_descriptions.${key}`),
      label: keyToLabel(key),
      pacing: cutline.cutoffLabel || null
    };
  } else {
    // No cutlines available - show no data instead of null/empty values
    return {
      percentile,
      color: MasteryPacingColor.NO_DATA,
      statusText: t('no_data'),
      description: t(`domain_descriptions.${key}`),
      label: keyToLabel(key),
      pacing: MasteryPacing.NO_DATA
    };
  }
}

export async function generateSubscores(classId, studentId, studentIds, useARM, locale, useLatestARM, startDate, endDate, districtId, schoolId, grade, period) {

  const cutlineMap = await fetchAllCutlines(districtId, schoolId, grade, period, locale);

  if (locale === "ES_MX") {
    const { readingRopeReport } = await SRS.getReadingRopeData({ studentId, locale, useLatestARM });
    const subscores = generateSubscoresFromReadingRope(readingRopeReport, locale, cutlineMap);
    return subscores;
  } else {
    const subscores = await generateSubscoresFromStudentRecordStore(studentId, locale, startDate, endDate, cutlineMap);
    return subscores;
  }
}

async function fetchAllCutlines(districtId, schoolId, grade, periodId, locale) {
  const cutlineMap = {};

  await Promise.all(SUBSCORE_SCALES.map(async (scale) => {
    const params = {
      districtId,
      schoolId,
      grade,
      scale,
      periodId: [periodId]
    };
    
    // Only add locale for Spanish, as English might not need it
    if (locale === "ES_MX") {
      params.locale = locale;
    }
    
    const cutlines = await SRS.getScaledCutlines(params);
    cutlineMap[scale] = cutlines;
  }));

  return cutlineMap;
}

export async function generateSubscoresFromStudentRecordStore(studentId, locale = "EN_US", startDate, endDate, cutlineMap) {
  const assessments = await SRS.getLatestAssessments({
    studentIds: [studentId],
    dateStart: startDate,
    dateEnd: endDate,
    locale
  });

  const latestAssessment = assessments?.[0];
  if (!latestAssessment?.scores) return null;

  const { scores } = latestAssessment;
  const t = i18next.getFixedT(null, 'teacher_parent_report');

  // Helper function that uses the extracted buildSubscore
  const buildSubscoreFromScores = (key, scoreKey) => {
    const percentile = scores[scoreKey];
    return buildSubscore(percentile, key, cutlineMap, t);
  };

  // Extract ARM score and PR
  const armScore = scores.armScore || null;
  const armScorePR = scores.armScorePR || null;
  const armCutline = armScorePR ? getPacingFromCutlines(armScorePR, cutlineMap['armScore'] || []) : null;

  const finalArmScore = {
    value: formatScoreByType(armScore, 'ARM'),
    percentile: armScorePR,
    pacing: (armCutline && armCutline.cutoffLabel) ? armCutline.cutoffLabel : MasteryPacing.NO_DATA,
    color: (armCutline && armCutline.hexCode) ? armCutline.hexCode : MasteryPacingColor.NO_DATA
  };

  return {
    decoding: buildSubscoreFromScores('decoding', 'decodeScore_PR'),
    phonologicalAwareness: buildSubscoreFromScores('phonologicalAwareness', 'phonologicalAwareness_PR'),
    highFrequencyWords: buildSubscoreFromScores('highFrequencyWords', 'esriScore_PR'),
    backgroundKnowledge: buildSubscoreFromScores('backgroundKnowledge', 'bkScore_PR'),
    structuresAndReasoning: buildSubscoreFromScores('structuresAndReasoning', 'compScore_PR'),
    vocabulary: buildSubscoreFromScores('vocabulary', 'vocabularyScreenerScore_PR'),
    armScore: finalArmScore,
    // Include ARM cutlines for thread averages to use
    _armCutlines: cutlineMap['armScore'] || []
  };
}

export function generateSubscoresFromReadingRope(readingRope, locale, cutlineMap) {
  if (!readingRope || !readingRope.threads?.length) return null;
  const domains = [].concat(...readingRope.threads.map(({ domains }) => domains || []));

  const result = {
    decoding: transformDomainToSubScore(domains.find(d => d.label === 'Decoding'), 'decoding', cutlineMap),
    phonologicalAwareness: transformDomainToSubScore(domains.find(d => d.label === 'Phonological Awareness'), 'phonologicalAwareness', cutlineMap),
    highFrequencyWords: transformDomainToSubScore(domains.find(d => d.label === 'Sight Recognition'), 'highFrequencyWords', cutlineMap),
    backgroundKnowledge: transformDomainToSubScore(domains.find(d => d.label === 'Background Knowledge'), 'backgroundKnowledge', cutlineMap),
    structuresAndReasoning: transformDomainToSubScore(domains.find(d => d.label === 'Structure & Reasoning'), 'structuresAndReasoning', cutlineMap),
    vocabulary: transformDomainToSubScore(domains.find(d => d.label === 'Vocabulary'), 'vocabulary', cutlineMap),
    // Include ARM cutlines for thread averages to use
    _armCutlines: cutlineMap['armScore'] || []
  };

  return result;
}

function transformDomainToSubScore(domain, key, cutlineMap) {
  if (!domain || !domain.mastery) return null;
  const { mastery } = domain;
  const t = i18next.getFixedT(null, 'teacher_parent_report');
  
  try {
    // Try to get cutline from API first
    const cutline = getPacingFromCutlines(mastery.value, cutlineMap[key] || []);
    
    // If we have cutlines from API, use them
    if (cutline && cutline.hexCode) {
      return {
        percentile: mastery.value,
        color: cutline.hexCode,
        statusText: t(cutline.cutoffLabel || ''),
        description: t(`domain_descriptions.${domain.label}`),
        label: domain.label,
        pacing: cutline.cutoffLabel || null
      };
    } else {
      // No cutlines available - show no data instead of incorrect data
      return {
        percentile: mastery.value,
        color: MasteryPacingColor.NO_DATA,
        statusText: t('no_data'),
        description: t(`domain_descriptions.${domain.label}`),
        label: domain.label,
        pacing: MasteryPacing.NO_DATA
      };
    }
  } catch (error) {
    console.error('Error getting cutlines for domain', key, error);
    // On error - show no data instead of incorrect data
    return {
      percentile: mastery.value,
      color: MasteryPacingColor.NO_DATA,
      statusText: t('no_data'),
      description: t(`domain_descriptions.${domain.label}`),
      label: domain.label,
      pacing: MasteryPacing.NO_DATA
    };
  }
}